'use client';
import { Label, RadioGroup } from '@headlessui/react';
import { useEffect, useMemo } from 'react';

import cn from '@/lib/clsxm';
import { priceFormatter } from '@/lib/helper';

import Container from '@/components/@base/container';
import { RadioOption } from '@/components/@base/input/radio';
import { Text } from '@/components/@base/typography';
import XImage from '@/components/@base/x-image';
import { useGetCart } from '@/components/app/cart/services/hooks';
import { useGetProfile } from '@/components/layout/services/hooks';

import { useCheckoutStore } from '@/store/checkout.store';

import localization from '@/constant/localization';

import { useFinalPrice } from '../hooks';

export default function Gateways() {
  const { data, isLoading } = useGetCart();
  const { profile } = useGetProfile();
  const { setGateway, gateway, useWallet } = useCheckoutStore();
  const finalPrice = useFinalPrice();

  const customerType = profile?.customerType;

  const gateways = useMemo(() => {
    if (!data?.gateways || !customerType) return [];
    return data.gateways[customerType] ?? [];
  }, [data?.gateways, customerType]);

  const filteredGateways = useMemo(() => {
    if (!gateways.length) return [];

    return gateways.filter((item) => {
      if (item !== 'snapp_pay') return true;

      const snappMeta = data?.snappPayMeta;
      if (!snappMeta) return true;

      return useWallet ? !!snappMeta.withWallet : !!snappMeta.withoutWallet;
    });
  }, [gateways, data?.snappPayMeta, useWallet]);

  useEffect(() => {
    if (!filteredGateways.length) return;

    if (!filteredGateways.includes(gateway)) {
      setGateway(filteredGateways[0]);
    }
  }, [filteredGateways]);

  if (!data || !profile || isLoading || finalPrice <= 0) {
    return null;
  }
  const installment = finalPrice > 0 ? finalPrice / 4 : 0;
  const gatewayCount = filteredGateways.length;

  return (
    <Container className='w-full p-[10px] pt-[23px] *:w-full md:px-0 md:pt-[5px]' center>
      <Container className='max-w-[350px] rounded-md border border-[#E5E8EB]'>
        <RadioGroup value={gateway} onChange={setGateway}>
          {filteredGateways.map((item, index) => (
            <RadioGroup.Option key={item} value={item}>
              {({ checked }) => (
                <Label
                  className={cn(
                    'cursor-pointer border-b border-b-[#E5E8EB] outline-hidden transition duration-300',
                    checked &&
                      'bg-[linear-gradient(180deg,#ffb6162b_0%,rgba(255,182,22,0)_100%)]',
                  )}
                >
                  <Container
                    center
                    className={cn(
                      'h-[68px] justify-between border border-white px-5 py-7',
                      checked && 'border-[#FFC87B]',
                      checked && gatewayCount === index + 1
                        ? 'rounded-br-md rounded-bl-md'
                        : checked && index === 0
                          ? 'rounded-tl-md rounded-tr-md'
                          : null,
                    )}
                  >
                    <Container center className='gap-[10px]'>
                      <RadioOption
                        value={item}
                        className={cn(
                          'group flex size-[18px] items-center justify-center rounded-full border border-[#E5E8EB] bg-white bg-clip-padding outline-hidden transition-colors duration-300 ease-in-out',
                          checked
                            ? 'border-none bg-[linear-gradient(247.65deg,#FFC107_-35.57%,#FF9737_100%)]'
                            : 'bg-white',
                        )}
                      >
                        <Container
                          className={cn(
                            'invisible size-[10px] rounded-full bg-white',
                            checked && 'visible',
                          )}
                        />
                      </RadioOption>
                      <Text className={cn('text-[#424244]', checked && 'font-bold')}>
                        {[
                          'zarinpal',
                          'payping',
                          'paystar',
                          'zarinpal_test',
                          'payapi',
                          'aminremit',
                        ].includes(item) ? (
                          localization.zhaketpay
                        ) : item === 'snapp_pay' ? (
                          <div>
                            <div className='text-[12px] leading-6 font-bold text-[#424244]'>
                              {localization.pay_with_snapp_pay}
                            </div>
                            <div className='text-[10px] leading-[22px] whitespace-nowrap text-[#5B5C60]'>
                              {localization.every_installment_with_snapp(
                                priceFormatter(installment),
                              )}
                            </div>
                          </div>
                        ) : (
                          localization[item]
                        )}
                      </Text>
                    </Container>

                    <XImage
                      src={`/images/bank-logo/${
                        ['zarinpal', 'payping', 'paystar', 'zarinpal_test', 'payapi', 'aminremit'].includes(
                          item,
                        )
                          ? 'zhaketpay'
                          : item
                      }-logo.svg`}
                      alt={localization[item]}
                      width={item === 'snapp_pay' ? 40 : item === 'yekta_pay' ? 50 : 60}
                      height={item === 'snapp_pay' ? 40 : item === 'yekta_pay' ? 35 : 30}
                    />
                  </Container>
                </Label>
              )}
            </RadioGroup.Option>
          ))}
        </RadioGroup>
      </Container>
    </Container>
  );
}
